import {
  battery,
  deliveryOption,
  internet,
  phone,
  points,
  staticIp,
} from '@/app/signup/(state)/CartStore';
import {store as questionaryStore} from '@/app/signup/(state)/PhoneQuestionnaireStore';
import {store as preconfigStore} from '@/app/signup/(state)/PreconfigStore';
import {store as scheduleStore} from '@/app/signup/(state)/ScheduleStore';
import {selfInstallKitWidget} from '@/app/signup/(state)/SelfInstallKitStore';
import type {Step} from '@/app/signup/routing/StepsRouting';
import type {AnalyticsData} from '@gfiber/typesmith';
import type {BuyflowType, CurrentStep} from '../TypesOpenApi';
import {timeInTimeZone} from '../utils/date-time';
import {openAPIFetchErrorParse} from './helper';

export const BACK_ANALYTICS_DATA_BY_PAGE: {
  [K in CurrentStep]?: [string, string] | ((s: Step) => [string, string]);
} = {
  INTERNET: ['pick_plan', ''],
  PHONE: ['phone', ''],
  BATTERY: ['phone', ''],
  PHONE_QUESTIONNAIRE: ['phone', ''],
  SELF_INSTALL_KIT: () => {
    // Get sik widget
    const sikDeliveryWigets = selfInstallKitWidget.deliveryOptionWidgets.map(
      ({deliveryType}) => deliveryType,
    );

    // Only SIK Shipping available according to one option
    if (sikDeliveryWigets?.length && sikDeliveryWigets?.length === 1) {
      return ['sik_shipping', ''];
    }

    const INTERNAL_TYPE_CATEGORY_MAP: {[key: string]: string} = {
      SHIPPING: 'sik_shipping',
      PICKUP: 'store_pickup',
    };
    // Default value is deliveryOption.productDeliveryChoice, such as doc doesnt provide
    // other cases except shipping and pickup
    const partOfCategory =
      INTERNAL_TYPE_CATEGORY_MAP[deliveryOption.productDeliveryChoice] ||
      deliveryOption.productDeliveryChoice;

    return [partOfCategory, ''];
  },
  SCHEDULING: ['installation', ''],
  STREAMING: ['streaming service', ''],
  REVIEW: ['review', ''],
  POINT_SELECTOR: ['cpe', ''],
  VERIFY: ['contact info', ''],
  STATIC_IP: ['static_ip_step', ''],
};

export const NEXT_ANALYTICS_DATA_BY_PAGE: {
  [K in CurrentStep]?:
    | [string, string]
    | ((s: Step) => [string, string, AnalyticsData?]);
} = {
  PRECONFIG: () => {
    const {PHONE_PRODUCT, STREAMING_PRODUCT} = preconfigStore;
    const label = 'internet';
    const productsSelected =
      PHONE_PRODUCT || STREAMING_PRODUCT
        ? `${PHONE_PRODUCT ? '-phone' : ''}${STREAMING_PRODUCT ? '-tv' : ''}`
        : '-only';

    return ['preconfig_step', `${label}${productsSelected}`];
  },
  INTERNET: () => {
    return ['pick_plan', internet._product!.value];
  },
  PHONE: () => {
    return ['phone', phone.isActualProduct ? 'phone' : 'no_phone'];
  },
  BATTERY: () => {
    return ['phone', battery.isActualProduct ? 'battery' : 'no_battery'];
  },
  PHONE_QUESTIONNAIRE: () => {
    const selectedCheckboxes = questionaryStore.selected.join(',');
    return ['phone', selectedCheckboxes];
  },
  SELF_INSTALL_KIT: () => {
    // Get sik widget
    const sikDeliveryWigets = selfInstallKitWidget.deliveryOptionWidgets.map(
      ({deliveryType}) => deliveryType,
    );

    // Only SIK Shipping available according to one option
    if (sikDeliveryWigets?.length && sikDeliveryWigets?.length === 1) {
      return ['sik_shipping', 'self install kit'];
    }

    const SIK_LABELING: {[key: string]: string} = {
      SHIPPING: 'self install kit',
      PICKUP: 'store pickup',
    };
    // Default value is deliveryOption.productDeliveryChoice, such as doc doesnt provide
    // other cases except shipping and pickup
    const sikLabel =
      SIK_LABELING[deliveryOption.productDeliveryChoice] ||
      deliveryOption.productDeliveryChoice;

    return ['store_pickup', sikLabel];
  },
  SCHEDULING: () => {
    let timestamp;

    if (scheduleStore.timeSlot && scheduleStore.timeSlot.startDateTime) {
      timestamp = timeInTimeZone(
        scheduleStore.timeSlot.startDateTime,
        scheduleStore.timeSlot.timezone || '',
      );
    }

    return [
      'installation',
      'schedule appointment',
      {
        selectedAppointment: timestamp?.toString() || '-',
      },
    ];
  },
  POINT_SELECTOR: () => {
    const pointsCount = [
      ...points.included.filter(({chargeType}) => chargeType === 'ONE_TIME'),
      ...points.additional.filter(({chargeType}) => chargeType === 'ONE_TIME'),
    ].reduce((acc, el) => (el.quantity || 0) + acc, 0);

    return ['cpe', `cpe points ${pointsCount}`];
  },
  REVIEW: ['review', 'checkout_step'],
  VERIFY: ['contact info', ''],
  WIFI_SETUP: ['instant_activation', ''],
  NOTIFICATION_PREFERENCES: ['confirmation', ''],
  STATIC_IP: () => {
    return ['static_ip_step', staticIp.productId || ''];
  },
};

type GetExeptionTextFn = (
  s: CurrentStep,
  t?: BuyflowType,
  error?: unknown,
) => string | null;

type Extra = {
  // is used as common provider of descriptionn for the step.
  // is called only if no other configuration are available
  UNIVERSAL?: GetExeptionTextFn;
};
export type ExceptionTrackingActions = {
  [k in CurrentStep]?: {
    [v in BuyflowType]?: string | GetExeptionTextFn;
  } & Extra;
};

export const EXCEPTIONAL_ACTIONS: ExceptionTrackingActions = {
  WIFI_SETUP: {
    INSTANT_ACTIVATION: (step, _, error) => {
      const errText = openAPIFetchErrorParse(error);

      return errText ?? (error as object).toString();
    },
  },
  BILLING: {
    UNIVERSAL: (step, type, error) => {
      if (type !== 'INSTANT_ACTIVATION') {
        const errText = openAPIFetchErrorParse(error);

        return errText ?? (error as object).toString();
      }

      return null;
    },
  },
};
